import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Building2, DoorOpen, Users, CreditCard,
  MessageSquare, ClipboardList, LogOut, Menu, X, ChevronRight, Wrench, Shield, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/sites', label: 'Sites', icon: Building2 },
  { to: '/studios', label: 'Studios', icon: DoorOpen },
  { to: '/enquiries', label: 'Pipeline', icon: ClipboardList },
  { to: '/occupiers', label: 'Occupiers', icon: Users },
  { to: '/payments', label: 'Payments', icon: CreditCard },
  { to: '/tickets', label: 'Support', icon: MessageSquare },
  { to: '/site-ops', label: 'Operations', icon: Wrench },
  { to: '/subscribers', label: 'Subscribers', icon: Mail },
  { to: '/security', label: 'Security', icon: Shield },
];

export default function OperatorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E2DC] z-50 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 hover:bg-[#F2F0EB] rounded-xl transition-colors" data-testid="mobile-menu-button">
          <Menu className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />
        </button>
        <span className="font-heading text-base font-medium text-[#3A4035]"><img src="/logo.webp" alt="Go Freestyle Studios" className="h-10" style={{filter:'contrast(1.15) brightness(0.95)'}} /></span>
        <div className="w-9" />
      </header>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <span className="font-heading text-lg font-medium text-[#3A4035]">Menu</span>
                <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-[#F2F0EB] rounded-lg" data-testid="close-sidebar-button">
                  <X className="w-5 h-5 text-[#5C5C55]" strokeWidth={1.5} />
                </button>
              </div>
              <nav className="space-y-1">
                {navItems.map(item => (
                  <NavLink key={item.to} to={item.to} onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB] hover:text-[#1A1A1A]'}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}>
                    <item.icon className="w-4 h-4" strokeWidth={1.5} />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-8 pt-6 border-t border-[#E5E2DC]">
                <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#5C5C55] hover:bg-[#F2F0EB] w-full" data-testid="logout-button">
                  <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-60 bg-white border-r border-[#E5E2DC] flex-col z-40">
        <div className="p-6 pb-4">
          <span className="font-heading text-lg font-medium text-[#3A4035]" data-testid="brand-sidebar"><img src="/logo.webp" alt="Go Freestyle Studios" className="h-[52px]" style={{filter:'contrast(1.15) brightness(0.95)'}} /></span>
          <p className="text-xs text-[#8A8B85] mt-1 capitalize">{user?.role?.replace('_', ' ')}</p>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive ? 'bg-[#3A4035] text-white shadow-sm' : 'text-[#5C5C55] hover:bg-[#F2F0EB] hover:text-[#1A1A1A]'}`}
              data-testid={`nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E5E2DC]">
          <div className="px-4 py-2 mb-2">
            <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</p>
            <p className="text-xs text-[#8A8B85] truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-[#5C5C55] hover:bg-[#F2F0EB] w-full transition-colors" data-testid="logout-button">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-60 pt-16 md:pt-0 min-h-screen">
        <div className="px-4 sm:px-8 md:px-10 py-6 md:py-10 max-w-7xl">
          <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
